import { useState, useEffect } from 'react';
import useSWR from 'swr';

// import { SESSION_TIMEOUT, FOCUS_THROTTLE_INTERVAL } from '@/config/appSettings';

import { IResponseError } from '@/lib/apiBase';
import { IRepository } from '@/types';
import {
  IGatedAccessPolicy,
  fetchGatedAccessPolicy,
  createGatedAccessPolicy,
  IGatedAccessPolicyResponse,
  TFormDefinition,
} from '@/lib/gatedAccess/gatedAccessPolicyApi';

const useGatedAccessPolicy = ({
  repository,
  initialPolicy,
}: {
  repository: IRepository;
  initialPolicy?: IGatedAccessPolicy | null;
}) => {
  const [gatedAccessPolicy, setGatedAccessPolicy] = useState<IGatedAccessPolicy | undefined | null>(initialPolicy);
  const [error, setError] = useState<IResponseError | undefined>();

  const fetchKey = ['gatedAccessPolicy', repository.namespace, repository.name];

  const {
    data,
    error: requestError,
    isLoading,
    isValidating,
    mutate,
  } = useSWR(fetchKey, () => fetchGatedAccessPolicy({ repository }), {
    fallback: {
      gatedAccessPolicy: initialPolicy,
      status: 'success',
      isLoading: false,
      statusCode: 200,
      status_message: 'OK',
    },
    keepPreviousData: true,
    revalidateOnFocus: false,
    revalidateOnMount: !initialPolicy,
    revalidateOnReconnect: false,
  });

  const revalidate = () => {
    mutate();
  };

  useEffect(() => {
    if (data) {
      setGatedAccessPolicy(data?.policy);
      setError(data?.error as IResponseError);
    }
  }, [data]);

  const createSuccess = (response: IGatedAccessPolicyResponse): void => {
    setError(undefined);
    const { policy } = response;
    console.log('createSuccess > policy', policy);
    if (policy) {
      setGatedAccessPolicy(policy);
      revalidate();
    }
  };

  const createFailed = (response: IGatedAccessPolicyResponse): void => {
    setError(response.error as IResponseError);
    setGatedAccessPolicy(undefined);
  };

  type createParams = {
    access_form_schema: TFormDefinition | null;
    license_statement: string;
    intro_title: string;
    intro_content: string;
    requires_approval: boolean;
  };

  const createPolicy = async ({
    access_form_schema,
    license_statement,
    intro_title,
    intro_content,
    requires_approval = false,
  }: createParams): Promise<string> => {
    const response = await createGatedAccessPolicy({
      repository,
      access_form_schema,
      license_statement,
      intro_title,
      intro_content,
      requires_approval,
    });

    if (response.status === 'success') {
      createSuccess(response);
    } else {
      createFailed(response);
    }
    return response?.status;
  };

  return { gatedAccessPolicy, error, revalidate, createPolicy, isLoading, isValidating };
};

export default useGatedAccessPolicy;
